import getScrollBarProps, { ScrollBarOptions } from "@/styles/scrollBarProps";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { v4 } from "uuid";

export interface UseScrollBarProps extends ScrollBarOptions {
  containerSelector?: string;
}
export function useScrollBar(options?: UseScrollBarProps) {
  const { containerSelector, ...scrollBarOptions } = options ?? {};
  const [scrollable, setScrollable] = useState(false);
  const elRef = useRef<Element | null>(null);
  const scrollNameRef = useRef<string>(`scroll-container__${v4()}`);

  const scrollBarProps = useMemo(() => {
    const props = getScrollBarProps({ ...scrollBarOptions, scrollable });
    return {
      ...props,
      "data-scroll-name": scrollNameRef.current,
    };
  }, [options, scrollable, scrollNameRef.current]);

  const scrollName = scrollBarProps["data-scroll-name"];

  useLayoutEffect(() => {
    const interval = setInterval(() => {
      const el = document.querySelector(containerSelector ?? `[data-scroll-name=${scrollName}]`);

      function detectScrollBar() {
        let hasScrollBar = false;
        if (el) {
          const { clientHeight, scrollHeight } = el;
          hasScrollBar = scrollHeight > clientHeight;
          if (hasScrollBar) {
            setScrollable(hasScrollBar);
            return;
          }
          // 兼容别的情况，但如果是上面条件检测不到的可滚动容器，可能会看到容器滚一下
          const initScrollTop = el.scrollTop ?? 0;
          if (initScrollTop > 0) {
            hasScrollBar = true;
          } else {
            el.scrollTop += 0.5; // 最少加0.5，否则不生效
            const curScrollTop = el.scrollTop;
            hasScrollBar = curScrollTop !== 0;
          }
          setScrollable(hasScrollBar);
        }
      }

      if (el && !elRef.current) {
        elRef.current = el;
        clearInterval(interval);

        if (el) {
          el!.removeEventListener("mouseenter", detectScrollBar);
          el!.removeEventListener("wheel", detectScrollBar);
          el!.removeEventListener("mousemove", detectScrollBar);

          el.addEventListener("mouseenter", detectScrollBar);
          el.addEventListener("wheel", detectScrollBar);
          el.addEventListener("mousemove", detectScrollBar);

          return () => {
            el!.removeEventListener("mouseenter", detectScrollBar);
            el!.removeEventListener("wheel", detectScrollBar);
            el!.removeEventListener("mousemove", detectScrollBar);
          };
        }
      }
    }, 100);
  }, [containerSelector, scrollName]);

  return {
    scrollable,
    scrollBarProps,
  };
}
